import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Tree,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import { DoubleLeftOutlined, DoubleRightOutlined, EditOutlined, LeftOutlined, RightOutlined, SettingOutlined } from '@ant-design/icons';
import { dmsAntdTheme } from '../../config/antdTheme';
import { getAllRtusData } from '../../services/rtuService';
import {
  assignRtuPingTemplateBulk,
  createRtuPingTemplate,
  listRtuPingAssignments,
  listRtuPingTemplates,
  updateRtuPingTemplate,
  unassignRtuPingTemplates,
} from '../../services/rtuPingSettingsService';
import { RtuPingTemplateAssignmentDto, RtuPingTemplateDto, RtuPingTemplateUpsertDto } from '../../types/RtuPingSettings';
import { RTU } from '../../types/RTU';
import { SkeletonRegion, SkeletonTable } from '../common/Skeleton/Skeleton';
import './AdminPingSettingsPage.css';

type TemplateFormValues = RtuPingTemplateUpsertDto;

interface RtuTreeRow {
  id: number;
  name: string;
  field: string;
  serialNo: string | null;
}

interface RtuSearchOption {
  value: number;
  label: string;
  rtuId: number;
  name: string;
  field: string;
}

const DEFAULT_TEMPLATE: TemplateFormValues = {
  name: '',
  enabled: true,
  intervalSeconds: 900,
  attemptCount: 3,
  timeoutMs: 2000,
  delayBetweenAttemptsMs: 500,
  onlineSuccessCount: 3,
  unstableMinSuccessCount: 1,
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const getSafeRequestMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || fallback;

const normalizeRtuName = (rtu: RTU): string => rtu.name?.trim() || `RTU ${rtu.id}`;

const isDefaultTemplate = (template?: Pick<RtuPingTemplateDto, 'name'> | null): boolean =>
  template?.name?.trim().toLowerCase() === 'default';

const templateSearchText = (template: RtuPingTemplateDto): string =>
  [
    template.name,
    `${Math.round(template.intervalSeconds / 60)} min`,
    `${template.attemptCount}`,
    `${template.timeoutMs}`,
    `${template.delayBetweenAttemptsMs}`,
    `${template.onlineSuccessCount}/${template.attemptCount}`,
    `${template.unstableMinSuccessCount}+`,
  ].join(' ').toLowerCase();

const toRtuSearchOption = (row: RtuTreeRow): RtuSearchOption => ({
  value: row.id,
  rtuId: row.id,
  label: `${row.name} | ${row.field}`,
  name: row.name,
  field: row.field,
});

const toRtuTreeRow = (rtu: RTU): RtuTreeRow => {
  const field = rtu.field?.trim() || 'Unassigned';

  return {
    id: rtu.id,
    name: normalizeRtuName(rtu),
    field,
    serialNo: null,
  };
};

const leafKeys = (keys: React.Key[]) => keys.map(String).filter((key) => !key.startsWith('field-')).map(Number);

const buildTree = (rows: RtuTreeRow[]): DataNode[] => {
  const groups = new Map<string, RtuTreeRow[]>();
  rows.forEach((row) => {
    const key = row.field || 'Unassigned';
    groups.set(key, [...(groups.get(key) ?? []), row]);
  });

  return Array.from(groups.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([field, fieldRows]) => ({
      title: field,
      key: `field-${field}`,
      children: fieldRows
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((row) => ({
          title: <span className="admin-ping-assignment-modal__rtu-name">{row.name}</span>,
          key: String(row.id),
        })),
    }));
};

const AdminPingSettingsPage = () => {
  const [templates, setTemplates] = useState<RtuPingTemplateDto[]>([]);
  const [assignments, setAssignments] = useState<RtuPingTemplateAssignmentDto[]>([]);
  const [rtus, setRtus] = useState<RTU[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [rtusLoading, setRtusLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<RtuPingTemplateDto | null>(null);
  const [assignmentTemplate, setAssignmentTemplate] = useState<RtuPingTemplateDto | null>(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [leftCheckedKeys, setLeftCheckedKeys] = useState<React.Key[]>([]);
  const [rightCheckedKeys, setRightCheckedKeys] = useState<React.Key[]>([]);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [selectedRtuId, setSelectedRtuId] = useState<number | null>(null);
  const [templateForm] = Form.useForm<TemplateFormValues>();
  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = loadSeqRef.current + 1;
    loadSeqRef.current = seq;
    setTemplatesLoading(true);
    setAssignmentsLoading(true);
    setRtusLoading(true);
    setError(null);

    const reportLoadError = (loadError: any, fallback: string) => {
      if (loadSeqRef.current === seq) {
        setError(getSafeRequestMessage(loadError, fallback));
      }
    };

    await Promise.allSettled([
      listRtuPingTemplates()
        .then((templateRows) => {
          if (loadSeqRef.current === seq) setTemplates(templateRows);
        })
        .catch((loadError) => reportLoadError(loadError, 'Ping templates could not be loaded. Please try again.'))
        .finally(() => {
          if (loadSeqRef.current === seq) setTemplatesLoading(false);
        }),
      listRtuPingAssignments()
        .then((assignmentRows) => {
          if (loadSeqRef.current === seq) setAssignments(assignmentRows);
        })
        .catch((loadError) => reportLoadError(loadError, 'Ping assignments could not be loaded. Please try again.'))
        .finally(() => {
          if (loadSeqRef.current === seq) setAssignmentsLoading(false);
        }),
      getAllRtusData()
        .then((rtuRows) => {
          if (loadSeqRef.current === seq) setRtus(Array.isArray(rtuRows) ? (rtuRows as RTU[]) : []);
        })
        .catch((loadError) => reportLoadError(loadError, 'RTUs could not be loaded. Please try again.'))
        .finally(() => {
          if (loadSeqRef.current === seq) setRtusLoading(false);
        }),
    ]);

  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const allRtuRows = useMemo(() => rtus.map(toRtuTreeRow), [rtus]);
  const rtuById = useMemo(() => new Map(allRtuRows.map((row) => [row.id, row])), [allRtuRows]);

  const defaultTemplate = useMemo(
    () => templates.find((template) => isDefaultTemplate(template)) ?? null,
    [templates]
  );

  const customAssignedRtuIds = useMemo(() => {
    const defaultTemplateId = defaultTemplate?.id;
    return new Set(
      assignments
        .filter((assignment) => assignment.templateId !== null && assignment.templateId !== undefined && assignment.templateId !== defaultTemplateId)
        .map((assignment) => assignment.currentRtuId)
    );
  }, [assignments, defaultTemplate]);

  const assignmentByRtuId = useMemo(() => {
    const map = new Map<number, RtuPingTemplateAssignmentDto>();
    assignments.forEach((assignment) => {
      map.set(assignment.currentRtuId, assignment);
    });
    return map;
  }, [assignments]);

  const selectedRtu = useMemo(
    () => (selectedRtuId === null ? null : rtuById.get(selectedRtuId) ?? null),
    [rtuById, selectedRtuId]
  );

  const selectedRtuAssignment = selectedRtuId === null ? null : assignmentByRtuId.get(selectedRtuId) ?? null;
  const selectedRtuTemplate = useMemo(() => {
    if (!selectedRtu) return null;
    const assignedTemplate = selectedRtuAssignment?.templateId
      ? templates.find((template) => template.id === selectedRtuAssignment.templateId) ?? null
      : null;
    return assignedTemplate ?? defaultTemplate;
  }, [defaultTemplate, selectedRtu, selectedRtuAssignment, templates]);

  const selectedRtuUsesCustomTemplate =
    Boolean(selectedRtuAssignment?.templateId && selectedRtuTemplate && !isDefaultTemplate(selectedRtuTemplate));

  const rtuSearchOptions = useMemo<RtuSearchOption[]>(() => {
    const query = templateSearch.trim().toLowerCase();
    if (selectedRtu) return [toRtuSearchOption(selectedRtu)];
    if (!query) return [];
    return allRtuRows
      .filter((row) =>
        row.name.toLowerCase().includes(query) ||
        row.field.toLowerCase().includes(query) ||
        String(row.id).includes(query)
      )
      .slice(0, 12)
      .map(toRtuSearchOption);
  }, [allRtuRows, selectedRtu, templateSearch]);

  const selectedTemplateAssignedIds = useMemo(() => {
    if (!assignmentTemplate) return new Set<number>();

    if (isDefaultTemplate(assignmentTemplate)) {
      return new Set(allRtuRows.filter((row) => !customAssignedRtuIds.has(row.id)).map((row) => row.id));
    }

    return new Set(assignments.filter((assignment) => assignment.templateId === assignmentTemplate.id).map((assignment) => assignment.currentRtuId));
  }, [allRtuRows, assignmentTemplate, assignments, customAssignedRtuIds]);

  const assignmentCountByTemplate = useMemo(() => {
    const counts = new Map<number, number>();
    assignments.forEach((assignment) => {
      if (assignment.templateId !== null && assignment.templateId !== undefined) {
        counts.set(assignment.templateId, (counts.get(assignment.templateId) ?? 0) + 1);
      }
    });
    if (defaultTemplate) {
      counts.set(defaultTemplate.id, allRtuRows.filter((row) => !customAssignedRtuIds.has(row.id)).length);
    }
    return counts;
  }, [allRtuRows, assignments, customAssignedRtuIds, defaultTemplate]);

  const availableRows = useMemo(() => {
    if (!assignmentTemplate) return [];
    return allRtuRows.filter((row) => !selectedTemplateAssignedIds.has(row.id));
  }, [allRtuRows, assignmentTemplate, selectedTemplateAssignedIds]);

  const assignedRows = useMemo(() => {
    if (!assignmentTemplate) return [];
    return allRtuRows.filter((row) => selectedTemplateAssignedIds.has(row.id));
  }, [allRtuRows, assignmentTemplate, selectedTemplateAssignedIds]);

  const filterRows = (rows: RtuTreeRow[], search: string) => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      row.name.toLowerCase().includes(query) ||
      row.field.toLowerCase().includes(query) ||
      String(row.id).includes(query) ||
      (row.serialNo || '').toLowerCase().includes(query)
    );
  };

  const leftTreeData = useMemo(() => buildTree(filterRows(availableRows, leftSearch)), [availableRows, leftSearch]);
  const rightTreeData = useMemo(() => buildTree(filterRows(assignedRows, rightSearch)), [assignedRows, rightSearch]);

  const openCreateTemplate = () => {
    setEditingTemplate(null);
    templateForm.setFieldsValue(DEFAULT_TEMPLATE);
    setTemplateModalOpen(true);
  };

  const openEditTemplate = (template: RtuPingTemplateDto) => {
    setEditingTemplate(template);
    templateForm.setFieldsValue({
      name: template.name,
      enabled: template.enabled,
      intervalSeconds: template.intervalSeconds,
      attemptCount: template.attemptCount,
      timeoutMs: template.timeoutMs,
      delayBetweenAttemptsMs: template.delayBetweenAttemptsMs,
      onlineSuccessCount: template.onlineSuccessCount,
      unstableMinSuccessCount: template.unstableMinSuccessCount,
    });
    setTemplateModalOpen(true);
  };

  const openAssignmentModal = (template: RtuPingTemplateDto) => {
    setAssignmentTemplate(template);
    setLeftCheckedKeys([]);
    setRightCheckedKeys([]);
    setLeftSearch('');
    setRightSearch('');
    setAssignmentModalOpen(true);
  };

  const saveTemplate = async () => {
    const values = await templateForm.validateFields();
    setSaving(true);
    try {
      if (editingTemplate) {
        await updateRtuPingTemplate(editingTemplate.id, values);
      } else {
        await createRtuPingTemplate(values);
      }
      setTemplateModalOpen(false);
      void message.success('Ping template saved');
      await load();
    } catch (saveError: any) {
      void message.error(getSafeRequestMessage(saveError, 'Template could not be saved. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const toTemplateUpsert = (template: RtuPingTemplateDto, enabled = template.enabled): RtuPingTemplateUpsertDto => ({
    name: template.name,
    enabled,
    intervalSeconds: template.intervalSeconds,
    attemptCount: template.attemptCount,
    timeoutMs: template.timeoutMs,
    delayBetweenAttemptsMs: template.delayBetweenAttemptsMs,
    onlineSuccessCount: template.onlineSuccessCount,
    unstableMinSuccessCount: template.unstableMinSuccessCount,
  });

  const toggleTemplateEnabled = async (template: RtuPingTemplateDto, enabled: boolean) => {
    setSaving(true);
    setTemplates((current) => current.map((row) => (row.id === template.id ? { ...row, enabled } : row)));
    try {
      const updated = await updateRtuPingTemplate(template.id, toTemplateUpsert(template, enabled));
      setTemplates((current) => current.map((row) => (row.id === template.id ? updated : row)));
      void message.success(enabled ? 'Ping template enabled' : 'Ping template disabled');
    } catch (saveError: any) {
      setTemplates((current) => current.map((row) => (row.id === template.id ? template : row)));
      void message.error(getSafeRequestMessage(saveError, 'Template status could not be changed. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const moveRight = async (ids: number[]) => {
    if (!assignmentTemplate || isDefaultTemplate(assignmentTemplate) || ids.length === 0) return;
    setSaving(true);
    try {
      await assignRtuPingTemplateBulk(assignmentTemplate.id, ids);
      setLeftCheckedKeys([]);
      void message.success('RTUs assigned to template');
      await load();
    } catch (assignError: any) {
      void message.error(getSafeRequestMessage(assignError, 'RTUs could not be assigned. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const moveLeft = async (ids: number[]) => {
    if (isDefaultTemplate(assignmentTemplate) || ids.length === 0) return;
    setSaving(true);
    try {
      await unassignRtuPingTemplates(ids);
      setRightCheckedKeys([]);
      void message.success('RTUs unassigned from template');
      await load();
    } catch (assignError: any) {
      void message.error(getSafeRequestMessage(assignError, 'RTUs could not be unassigned. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const clearSelectedRtuAssignment = async () => {
    if (!selectedRtu || !selectedRtuUsesCustomTemplate) return;
    setSaving(true);
    try {
      await unassignRtuPingTemplates([selectedRtu.id]);
      void message.success(`${selectedRtu.name} now uses Default`);
      await load();
    } catch (assignError: any) {
      void message.error(getSafeRequestMessage(assignError, 'Assignment could not be cleared. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const clearTemplateSearch = () => {
    setTemplateSearch('');
    setSelectedRtuId(null);
  };

  const filteredTemplates = useMemo(() => {
    if (selectedRtu) {
      return selectedRtuTemplate ? [selectedRtuTemplate] : [];
    }

    const query = templateSearch.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter((template) => templateSearchText(template).includes(query));
  }, [selectedRtu, selectedRtuTemplate, templateSearch, templates]);

  const selectedRtuTemplateLabel = selectedRtuTemplate?.name ?? 'Default template unavailable';

  const templateColumns: ColumnsType<RtuPingTemplateDto> = [
    {
      title: 'Active',
      dataIndex: 'enabled',
      width: 82,
      fixed: 'left',
      render: (value: boolean, row) => (
        <Switch
          size="small"
          checked={value}
          loading={saving}
          onChange={(checked) => toggleTemplateEnabled(row, checked)}
          aria-label={`${value ? 'Disable' : 'Enable'} ${row.name}`}
        />
      ),
    },
    { title: 'Name', dataIndex: 'name', width: 180 },
    { title: 'Interval', dataIndex: 'intervalSeconds', width: 110, render: (value: number) => `${Math.round(value / 60)} min` },
    { title: 'Attempts', dataIndex: 'attemptCount', width: 95 },
    { title: 'Timeout', dataIndex: 'timeoutMs', width: 105, render: (value: number) => `${value} ms` },
    { title: 'Delay', dataIndex: 'delayBetweenAttemptsMs', width: 95, render: (value: number) => `${value} ms` },
    { title: 'Success Required', dataIndex: 'onlineSuccessCount', width: 135, render: (value: number, row) => `${value}/${row.attemptCount}` },
    { title: 'Partial Success Min', dataIndex: 'unstableMinSuccessCount', width: 145, render: (value: number) => `${value}+` },
    { title: 'Assigned RTUs', width: 115, render: (_value, row) => assignmentCountByTemplate.get(row.id) ?? 0 },
    {
      title: 'Last Scheduled Check',
      dataIndex: 'lastScheduledCheckUtc',
      width: 170,
      render: formatDate,
    },
    {
      title: 'Next Expected Check',
      dataIndex: 'nextExpectedCheckUtc',
      width: 170,
      render: formatDate,
    },
    { title: 'Updated', dataIndex: 'updatedAtUtc', width: 170, render: formatDate },
    {
      title: 'Actions',
      width: 92,
      fixed: 'right',
      render: (_value, row) => (
        <Space size={4}>
          <Tooltip title={isDefaultTemplate(row) ? 'View assigned RTUs' : 'Assign RTUs'}>
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => openAssignmentModal(row)}
              aria-label={isDefaultTemplate(row) ? `View RTUs assigned to ${row.name}` : `Assign RTUs to ${row.name}`}
            />
          </Tooltip>
          <Tooltip title="Edit template">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditTemplate(row)} aria-label={`Edit ${row.name}`} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const assignmentColumns: ColumnsType<RtuPingTemplateAssignmentDto> = [
    {
      title: 'RTU',
      dataIndex: 'currentRtuId',
      width: 180,
      render: (value: number) => rtuById.get(value)?.name || `RTU ${value}`,
    },
    { title: 'Serial Number', dataIndex: 'rtuSerialNo', width: 160, render: (value) => value || '-' },
    { title: 'Template', dataIndex: 'templateName', width: 180 },
    { title: 'Assigned', dataIndex: 'assignedAtUtc', width: 180, render: formatDate },
    { title: 'Assigned By', dataIndex: 'assignedBy', render: (value) => value || '-' },
  ];

  const isAssignmentTemplateDefault = isDefaultTemplate(assignmentTemplate);
  const assignmentModalTitle = assignmentTemplate
    ? `${isAssignmentTemplateDefault ? 'Assigned RTUs' : 'Assign / Unassign RTUs'} - ${assignmentTemplate.name}`
    : 'Assign / Unassign RTUs';
  const showTemplatesSkeleton = templatesLoading && templates.length === 0;
  const showAssignmentsSkeleton = assignmentsLoading && assignments.length === 0;

  return (
    <ConfigProvider theme={dmsAntdTheme}>
      <div className="admin-ping-settings">
        {error ? <Alert type="error" message={error} showIcon className="admin-ping-settings__alert" /> : null}

        <Tabs
          items={[
            {
              key: 'templates',
              label: 'Ping templates',
              children: (
                <Card
                  size="small"
                  title="Ping templates"
                  extra={<Button type="primary" onClick={openCreateTemplate}>New Template</Button>}
                >
                  <div className="admin-ping-settings__template-toolbar">
                    <Select
                      allowClear
                      showSearch={!selectedRtu}
                      suffixIcon={null}
                      className="admin-ping-settings__template-search"
                      options={rtuSearchOptions}
                      value={selectedRtuId ?? undefined}
                      searchValue={selectedRtu ? '' : templateSearch}
                      filterOption={false}
                      open={selectedRtu ? false : undefined}
                      optionRender={(option) => (
                        <div className="admin-ping-settings__rtu-option">
                          <span>{(option.data as RtuSearchOption).name}</span>
                          <small>{(option.data as RtuSearchOption).field}</small>
                        </div>
                      )}
                      onSearch={(value) => {
                        if (!selectedRtu) setTemplateSearch(value);
                      }}
                      onChange={(value) => {
                        if (value === undefined || value === null) {
                          clearTemplateSearch();
                          return;
                        }
                        setSelectedRtuId(value);
                      }}
                      onClear={clearTemplateSearch}
                      placeholder="Find RTU or template"
                      notFoundContent={templateSearch.trim() ? 'No matching RTUs' : null}
                    />
                    {selectedRtu ? (
                      <div className="admin-ping-settings__template-context">
                        <span className="admin-ping-settings__effective-template">
                          Effective template: <strong>{selectedRtuTemplateLabel}</strong>
                        </span>
                        <Tag color={selectedRtuUsesCustomTemplate ? 'blue' : 'default'}>
                          {selectedRtuUsesCustomTemplate ? 'Custom' : 'Default'}
                        </Tag>
                        {selectedRtuUsesCustomTemplate ? (
                          <Popconfirm
                            title="Clear template assignment?"
                            description={`${selectedRtu.name} will stop using ${selectedRtuTemplateLabel} and fall back to the Default template.`}
                            okText="Clear Assignment"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true, loading: saving }}
                            onConfirm={clearSelectedRtuAssignment}
                          >
                            <Button size="small" loading={saving}>
                              Clear Assignment
                            </Button>
                          </Popconfirm>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                    {showTemplatesSkeleton ? (
                      <SkeletonRegion label="Loading ping templates">
                        <SkeletonTable columns={[0.6, 1.2, 0.8, 0.7, 0.8, 0.7, 1, 1, 1, 0.8, 1.2]} rows={6} />
                      </SkeletonRegion>
                    ) : (
                      <Table
                        rowKey="id"
                        size="small"
                        columns={templateColumns}
                        dataSource={filteredTemplates}
                        scroll={{ x: 1260 }}
                        rowClassName={(row) => (selectedRtuTemplate?.id === row.id ? 'admin-ping-settings__template-row--matched' : '')}
                      />
                    )}
                </Card>
              ),
            },
            {
              key: 'assignments',
              label: 'Ping Assignments',
              children: (
                <Card size="small" title="Current Template Assignments">
                  {showAssignmentsSkeleton ? (
                    <SkeletonRegion label="Loading ping assignments">
                      <SkeletonTable columns={[1.2, 1, 1.2, 1, 1]} rows={6} />
                    </SkeletonRegion>
                  ) : (
                    <Table rowKey="id" size="small" columns={assignmentColumns} dataSource={assignments} />
                  )}
                </Card>
              ),
            },
          ]}
        />

        <Modal
          title={editingTemplate ? `Edit ${editingTemplate.name}` : 'New Ping Template'}
          open={templateModalOpen}
          onCancel={() => setTemplateModalOpen(false)}
          onOk={saveTemplate}
          confirmLoading={saving}
          okText="Save"
          width={720}
          destroyOnHidden
        >
          <Form form={templateForm} layout="vertical" initialValues={DEFAULT_TEMPLATE} className="admin-ping-settings__template-form">
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Template name is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="intervalSeconds" label="Interval (seconds)" rules={[{ required: true }]}>
              <InputNumber min={60} max={86400} />
            </Form.Item>
            <Form.Item name="attemptCount" label="Attempts" rules={[{ required: true }]}>
              <InputNumber min={1} max={10} />
            </Form.Item>
            <Form.Item name="timeoutMs" label="Timeout (ms)" rules={[{ required: true }]}>
              <InputNumber min={500} max={10000} step={500} />
            </Form.Item>
            <Form.Item name="delayBetweenAttemptsMs" label="Delay Between Attempts (ms)" rules={[{ required: true }]}>
              <InputNumber min={0} max={10000} step={250} />
            </Form.Item>
            <Form.Item name="onlineSuccessCount" label="Success Required" rules={[{ required: true }]}>
              <InputNumber min={1} max={10} />
            </Form.Item>
            <Form.Item name="unstableMinSuccessCount" label="Partial Success Minimum" rules={[{ required: true }]}>
              <InputNumber min={1} max={10} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={assignmentModalTitle}
          open={assignmentModalOpen}
          onCancel={() => setAssignmentModalOpen(false)}
          footer={<Button onClick={() => setAssignmentModalOpen(false)}>Close</Button>}
          width="min(1080px, calc(100vw - 96px))"
          style={{ top: 56 }}
          className="admin-ping-assignment-modal"
          destroyOnHidden
        >
          {isAssignmentTemplateDefault ? (
            <Space direction="vertical" size={12} className="admin-ping-settings__stack">
              <Alert
                type="info"
                showIcon
                message="RTUs shown here have no custom ping template assignment, so they use the Default template."
              />
              <Input.Search placeholder="Search field or RTU" allowClear value={rightSearch} onChange={(event) => setRightSearch(event.target.value)} />
              <div className="admin-ping-assignment-modal__tree">
                <Tree virtual selectable={false} defaultExpandAll treeData={rightTreeData} />
              </div>
            </Space>
          ) : (
            <>
              <Alert
                type="warning"
                showIcon
                className="admin-ping-assignment-modal__warning"
                message="When you assign RTUs to this template, they will be automatically unassigned from any other ping template. Each RTU can only use one ping template at a time."
              />
              <div className="admin-ping-assignment-modal__content">
                <div className="admin-ping-assignment-modal__pane">
                  <Typography.Title level={5}>Available RTUs</Typography.Title>
                  <Input.Search placeholder="Search field or RTU" allowClear value={leftSearch} onChange={(event) => setLeftSearch(event.target.value)} />
                  <div className="admin-ping-assignment-modal__tree">
                    <Tree
                      virtual
                      checkable
                      disabled={rtusLoading}
                      selectable={false}
                      multiple
                      defaultExpandAll
                      treeData={leftTreeData}
                      checkedKeys={leftCheckedKeys}
                      onCheck={(keys) => setLeftCheckedKeys(Array.isArray(keys) ? keys : keys.checked)}
                    />
                  </div>
                </div>
                <div className="admin-ping-assignment-modal__actions">
                  <Tooltip title="Assign selected">
                    <Button icon={<RightOutlined />} disabled={saving || leafKeys(leftCheckedKeys).length === 0} onClick={() => moveRight(leafKeys(leftCheckedKeys))} />
                  </Tooltip>
                  <Tooltip title="Unassign selected">
                    <Button icon={<LeftOutlined />} disabled={saving || leafKeys(rightCheckedKeys).length === 0} onClick={() => moveLeft(leafKeys(rightCheckedKeys))} />
                  </Tooltip>
                  <Tooltip title="Assign all available">
                    <Button icon={<DoubleRightOutlined />} disabled={saving || availableRows.length === 0} onClick={() => moveRight(availableRows.map((row) => row.id))} />
                  </Tooltip>
                  <Tooltip title="Unassign all">
                    <Button icon={<DoubleLeftOutlined />} disabled={saving || assignedRows.length === 0} onClick={() => moveLeft(assignedRows.map((row) => row.id))} />
                  </Tooltip>
                </div>
                <div className="admin-ping-assignment-modal__pane">
                  <Typography.Title level={5}>Assigned RTUs</Typography.Title>
                  <Input.Search placeholder="Search field or RTU" allowClear value={rightSearch} onChange={(event) => setRightSearch(event.target.value)} />
                  <div className="admin-ping-assignment-modal__tree">
                    <Tree
                      virtual
                      checkable
                      disabled={rtusLoading}
                      selectable={false}
                      multiple
                      defaultExpandAll
                      treeData={rightTreeData}
                      checkedKeys={rightCheckedKeys}
                      onCheck={(keys) => setRightCheckedKeys(Array.isArray(keys) ? keys : keys.checked)}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default AdminPingSettingsPage;
